import { type ReactNode } from "react";

import { customTv } from "#utils";
import {
  Tabs as ReactAriaTabs,
  TabList as ReactAriaTabsList,
  Tab as ReactAriaTab,
  TabPanel as ReactAriaTabPanel,
  type TabsProps as ReactAriaTabsProps,
  type TabListProps as ReactAriaTabsListProps,
  type TabProps as ReactAriaTabProps,
  type TabPanelProps as ReactAriaTabPanelProps,
} from "react-aria-components";

/**
 * Outer container for the Tabs
 */
export interface TabsProps extends ReactAriaTabsProps {
  className?: string;
}

export function Tabs({ ...props }: TabsProps) {
  return <ReactAriaTabs {...props}>{props.children}</ReactAriaTabs>;
}

/**
 * Inner container for the tab content
 */
export interface TabPanelProps extends ReactAriaTabPanelProps {
  className?: string;
}

export function TabPanel({ children, ...props }: TabPanelProps) {
  return (
    <ReactAriaTabPanel {...props}>
      {(renderProps) => {
        // react-aria keeps a just-deselected panel mounted for one extra render to
        // run its exit animation — and `useExitAnimation` reports `isExiting` for that
        // render even when no animation is defined. During it, `state.selectedKey` is
        // already the NEW tab, so any data-bound children render against parent state
        // that no longer matches this panel (e.g. a Relay fragment dropped via @skip),
        // which crashes. Only render children when this panel is genuinely active so
        // consumers don't have to guard every panel themselves. Similar to MUI Tabs.
        const isActive = props.shouldForceMount || renderProps.state.selectedKey === props.id;
        if (!isActive) {
          return null;
        }
        return typeof children === "function" ? children(renderProps) : children;
      }}
    </ReactAriaTabPanel>
  );
}

/**
 * Inner container for the tab list
 */
const tabsStyles = customTv({
  base: "flex gap-x-0-5 mb-1-5",
});

export interface TabListProps<T extends object> extends ReactAriaTabsListProps<T> {
  className?: string;
  children?: ReactNode;
}

export function TabList<T extends object>({ className, children, ...props }: TabListProps<T>) {
  return (
    <ReactAriaTabsList className={tabsStyles({ className })} {...props}>
      {children}
    </ReactAriaTabsList>
  );
}

/**
 * The Tab component
 */

const tabStyles = customTv({
  base: "px-8 py-xxs text-label-medium-strong text-text-primary border-b-transparent outline-none transition-all ease-in-out cursor-pointer data-[selected]:text-text-brand border-b-2 data-[selected]:border-b-brand-primary hover:!text-stacklet-orange-700",
  variants: {
    isDisabled: {
      true: "text-text-disabled hover:!text-text-disabled",
      false: "",
    },
  },
  defaultVariants: {
    isDisabled: false,
  },
});

export interface TabProps extends ReactAriaTabProps {
  className?: string;
  isDisabled?: boolean;
}
export function Tab({ className, ...props }: TabProps) {
  return (
    <ReactAriaTab
      className={tabStyles({
        isDisabled: props.isDisabled,
        className,
      })}
      {...props}
    >
      {props.children}
    </ReactAriaTab>
  );
}
